import { Typography } from '@/components/primitives/Typography';
import { useThemedColors } from '@/hooks/useThemedColors';
import { cn } from '@/utils/cn';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import * as React from 'react';

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn('flex w-fit items-center justify-center', className)}
    {...props}
  >
    {children}
  </ToggleGroupPrimitive.Root>
));

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>
>(({ children, className, ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Item ref={ref} className={className} {...props}>
      <Typography variant="body1-bold" asChild>
        {children}
      </Typography>
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export interface ToggleSelectorOption {
  value: string;
  label: React.ReactNode;
}

interface ToggleSelectorProps {
  options: Array<ToggleSelectorOption>;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  'data-testid'?: string;
}

function ToggleSelectorComponent(
  {
    options,
    value,
    onChange,
    className,
    'data-testid': dataTestId,
  }: ToggleSelectorProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { getToggleSelectorColors, getGlobalStyles } = useThemedColors();

  return (
    <ToggleGroup
      ref={ref}
      type="single"
      value={value}
      onValueChange={(value: string | undefined) => {
        if (value) onChange?.(value);
      }}
      className={className}
      data-testid={dataTestId}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          className={cn(
            'relative border border-r-0 px-4 py-1.5 first:rounded-l-lg last:rounded-r-lg last:border-r hover:z-10 hover:border-r focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
            getToggleSelectorColors(),
            getGlobalStyles()
          )}
          data-testid={`${dataTestId}-option-${option.value}`}
        >
          <Typography variant="body1-bold" asChild>
            <span>{option.label}</span>
          </Typography>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

ToggleSelectorComponent.displayName = 'ToggleSelector';

export const ToggleSelector = React.forwardRef(ToggleSelectorComponent);
